import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { useBankSettings } from "@/hooks/useBankSettings";

export default function AdminBankSettings() {
  const { settings, loading, refetch } = useBankSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!loading) setForm(settings);
  }, [loading, settings]);

  const update = (k: keyof typeof form, v: any) => setForm((s) => ({ ...s, [k]: v }));

  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `bank-qr-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("brand-assets").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("brand-assets").getPublicUrl(path);
      update("qr_image_url", data.publicUrl);
      toast.success("QR code uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: err instanceof Error ? err.message : "" });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("app_settings")
        .upsert({ key: "bank_payment", value: form as any }, { onConflict: "key" });
      if (error) throw error;
      toast.success("Bank settings saved");
      refetch();
    } catch (err) {
      toast.error("Save failed", { description: err instanceof Error ? err.message : "" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin" />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bank Transfer Settings</CardTitle>
        <CardDescription>Configure the bank account and QR code shown to users paying via bank transfer.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <p className="font-medium text-sm">Enable Bank Transfer</p>
            <p className="text-xs text-muted-foreground">Show the "Pay via Bank Transfer" option to users.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => update("enabled", v)} />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Bank Name</Label>
            <Input value={form.bank_name} onChange={(e) => update("bank_name", e.target.value)} />
          </div>
          <div>
            <Label>Account Holder Name</Label>
            <Input value={form.account_name} onChange={(e) => update("account_name", e.target.value)} />
          </div>
          <div>
            <Label>Account Number</Label>
            <Input value={form.account_number} onChange={(e) => update("account_number", e.target.value)} />
          </div>
          <div>
            <Label>IBAN (optional)</Label>
            <Input value={form.iban} onChange={(e) => update("iban", e.target.value)} />
          </div>
          <div>
            <Label>SWIFT / BIC (optional)</Label>
            <Input value={form.swift} onChange={(e) => update("swift", e.target.value)} />
          </div>
          <div>
            <Label>Subscription Duration (days)</Label>
            <Input
              type="number"
              min={1}
              value={form.duration_days}
              onChange={(e) => update("duration_days", parseInt(e.target.value) || 30)}
            />
          </div>
        </div>

        <div>
          <Label>Payment Instructions (shown to users)</Label>
          <Textarea
            value={form.instructions}
            onChange={(e) => update("instructions", e.target.value)}
            rows={3}
            placeholder="e.g. Please include your email as reference. Payments processed within 24 hours."
          />
        </div>

        <div>
          <Label>QR Code Image</Label>
          <div className="flex items-center gap-3 mt-1">
            {form.qr_image_url && (
              <img src={form.qr_image_url} alt="QR" className="w-24 h-24 object-contain bg-white rounded border p-1" />
            )}
            <div>
              <Input type="file" accept="image/*" onChange={handleQrUpload} disabled={uploading} />
              {uploading && <p className="text-xs text-muted-foreground mt-1"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Uploading…</p>}
            </div>
          </div>
        </div>

        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </CardContent>
    </Card>
  );
}
